import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useSeo } from "@/lib/seo";
import { Section, Overline } from "@/components/shared";
import * as BN from "@/lib/booleanNetwork";
import { Play, RotateCcw, ArrowLeft, Activity, GitCommitVertical, Grid3x3, X, Shuffle, Download } from "lucide-react";

const CAT_COLOR = {
  input: "#6A7B8C",
  signaling: "#8AA0B8",
  metabolic: "#7FB0A0",
  stress: "#D08B8B",
  fate: "#C2F751",
};
const ACID = "#C2F751";

function NetworkCanvas({ state, width }) {
  const ref = useRef(null);
  const [hover, setHover] = useState(null);
  const H = 520;
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !width) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = width * dpr;
    canvas.height = H * dpr;
    canvas.style.width = width + "px";
    canvas.style.height = H + "px";
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, H);

    const pos = BN.computeLayout(width, H);

    // column headers
    ctx.font = "10px 'JetBrains Mono', monospace";
    ctx.textAlign = "center";
    const cols = ["input", "signaling", "metabolic", "stress", "fate"];
    const padX = 60;
    const usableW = width - padX * 2;
    cols.forEach((c, i) => {
      const x = padX + (usableW * i) / (cols.length - 1);
      ctx.fillStyle = "rgba(255,255,255,0.35)";
      ctx.fillText(BN.CATEGORY_LABELS[c].toUpperCase(), x, 18);
    });

    // edges
    BN.EDGES.forEach(([a, b]) => {
      const pa = pos[a], pb = pos[b];
      if (!pa || !pb) return;
      const on = state && state[a];
      ctx.beginPath();
      ctx.moveTo(pa.x, pa.y);
      const mx = (pa.x + pb.x) / 2;
      ctx.bezierCurveTo(mx, pa.y, mx, pb.y, pb.x, pb.y);
      ctx.strokeStyle = on ? "rgba(194,247,81,0.28)" : "rgba(255,255,255,0.055)";
      ctx.lineWidth = on ? 1.1 : 0.7;
      ctx.stroke();
    });

    // nodes
    BN.ALL_NODES.forEach((node) => {
      const p = pos[node];
      if (!p) return;
      const on = state && state[node];
      const r = p.cat === "fate" ? 8 : 5.5;
      ctx.beginPath();
      ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      ctx.fillStyle = on ? ACID : "#20242a";
      ctx.fill();
      ctx.lineWidth = hover === node ? 2 : 1.2;
      ctx.strokeStyle = hover === node ? "#fff" : on ? ACID : CAT_COLOR[p.cat];
      ctx.stroke();
      if (on) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, r + 3, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(194,247,81,0.25)";
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    });

    // labels for inputs + key nodes
    ctx.font = "9px 'JetBrains Mono', monospace";
    const labelSet = new Set([...BN.INPUT_NODES, ...BN.KEY_NODES]);
    BN.ALL_NODES.forEach((node) => {
      if (!labelSet.has(node)) return;
      const p = pos[node];
      const on = state && state[node];
      ctx.fillStyle = on ? "rgba(194,247,81,0.9)" : "rgba(255,255,255,0.45)";
      ctx.textAlign = p.cat === "input" ? "right" : p.cat === "fate" ? "left" : "center";
      const dx = p.cat === "input" ? -10 : p.cat === "fate" ? 12 : 0;
      const dy = p.cat === "input" || p.cat === "fate" ? 3 : -10;
      ctx.fillText(node, p.x + dx, p.y + dy);
    });
  }, [state, width, hover]);

  const onMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const mx = e.clientX - rect.left, my = e.clientY - rect.top;
    const pos = BN.computeLayout(width, H);
    let found = null;
    for (const node of BN.ALL_NODES) {
      const p = pos[node];
      if (Math.hypot(p.x - mx, p.y - my) < 9) { found = { node, x: p.x, y: p.y, cat: p.cat }; break; }
    }
    setHover(found ? found.node : null);
  };

  const hp = hover ? BN.computeLayout(width, H)[hover] : null;
  return (
    <div className="relative">
      <canvas ref={ref} data-testid="network-canvas" onMouseMove={onMove} onMouseLeave={() => setHover(null)} className="cursor-crosshair" />
      {hover && hp && (
        <div
          data-testid="node-tooltip"
          className="absolute pointer-events-none bg-black border border-acid/50 px-3 py-2 z-10"
          style={{ left: Math.min(hp.x + 12, width - 150), top: hp.y - 10 }}
        >
          <div className="font-mono-sci text-xs text-acid">{hover}</div>
          <div className="font-mono-sci text-[10px] text-white/50">{BN.CATEGORY_LABELS[hp.cat]} · {state && state[hover] ? "ON" : "OFF"}</div>
        </div>
      )}
    </div>
  );
}

function HeatmapCanvas({ history, currentStep, nodes, width }) {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !width || !history.length) return;
    const gutter = 118;
    const cols = history.length;
    const cell = Math.max(4, Math.min(16, Math.floor((width - gutter) / cols)));
    const rowH = 13;
    const H = nodes.length * rowH + 8;
    const dpr = window.devicePixelRatio || 1;
    const W = gutter + cols * cell;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    canvas.style.width = W + "px";
    canvas.style.height = H + "px";
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    ctx.font = "9px 'JetBrains Mono', monospace";
    ctx.textAlign = "right";
    nodes.forEach((node, r) => {
      const y = 8 + r * rowH;
      const cat = BN.getNodeCategory(node);
      ctx.fillStyle = CAT_COLOR[cat] || "#888";
      ctx.fillText(node, gutter - 8, y + rowH - 4);
      history.forEach((st, t) => {
        const on = st[node];
        const reveal = currentStep < 0 || t <= currentStep;
        ctx.fillStyle = on
          ? reveal ? ACID : "rgba(194,247,81,0.12)"
          : reveal ? "#23272e" : "rgba(255,255,255,0.03)";
        ctx.fillRect(gutter + t * cell, y, cell - 1, rowH - 2);
      });
    });
    if (currentStep >= 0) {
      ctx.strokeStyle = "rgba(255,255,255,0.5)";
      ctx.lineWidth = 1;
      ctx.strokeRect(gutter + currentStep * cell - 0.5, 4, cell, H - 8);
    }
  }, [history, currentStep, nodes, width]);

  return <canvas ref={ref} data-testid="heatmap-canvas" />;
}

const FATES = [
  { key: "PROLIFERATION", label: "Proliferation" },
  { key: "SENESCENCE", label: "Senescence" },
  { key: "APOPTOSIS", label: "Apoptosis" },
  { key: "SASP", label: "SASP" },
];

export default function Simulator() {
  useSeo(
    "Beyond Life Simulator | Boolean Network of Cellular Aging | Systems Beyond",
    "Interactive Boolean network prototype from the Beyond Life program. Explore how intracellular and environmental signals drive cellular states — proliferation, senescence, apoptosis and SASP."
  );

  const [inputs, setInputs] = useState({ ...BN.PRESETS.proliferative.inputs });
  const [activePreset, setActivePreset] = useState("proliferative");
  const [maxSteps, setMaxSteps] = useState(50);
  const [speed, setSpeed] = useState(180);
  const [tab, setTab] = useState("network");
  const [keyOnly, setKeyOnly] = useState(false);
  const [forced, setForced] = useState({});
  const [pertNode, setPertNode] = useState("");
  const [sample, setSample] = useState(null);
  const [lastRunKey, setLastRunKey] = useState("");

  const [history, setHistory] = useState([]);
  const [attractor, setAttractor] = useState([]);
  const [attractorStart, setAttractorStart] = useState(null);
  const [step, setStep] = useState(-1);
  const [playing, setPlaying] = useState(false);

  const timer = useRef(null);
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const measure = () => wrapRef.current && setWidth(wrapRef.current.clientWidth);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [tab]);

  const current = step >= 0 && history[step] ? history[step] : (attractor[0] || null);

  const toggle = (node) => {
    setInputs((p) => ({ ...p, [node]: !p[node] }));
    setActivePreset(null);
  };
  const applyPreset = (key) => {
    setInputs({ ...BN.PRESETS[key].inputs });
    setActivePreset(key);
  };

  const stopAnim = () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    setPlaying(false);
  };

  const run = useCallback(() => {
    stopAnim();
    const initial = BN.makeInitialState(inputs, false);
    const f = Object.keys(forced).length ? forced : null;
    const res = BN.simulate(initial, maxSteps, f);
    setHistory(res.history);
    setAttractor(res.attractor);
    setAttractorStart(res.attractorStart);
    setStep(0);
    setPlaying(true);
    setLastRunKey(JSON.stringify({ inputs, forced }));
  }, [inputs, maxSteps, forced]);

  const reset = () => {
    stopAnim();
    setHistory([]); setAttractor([]); setAttractorStart(null); setStep(-1); setSample(null);
  };

  const addPert = (value) => {
    if (!pertNode) return;
    setForced((p) => ({ ...p, [pertNode]: value }));
    setPertNode("");
  };
  const removePert = (node) => setForced((p) => { const n = { ...p }; delete n[node]; return n; });

  const runSample = () => {
    const f = Object.keys(forced).length ? forced : null;
    setSample(BN.sampleAttractors(inputs, f, 60, 60));
  };

  const exportRun = () => {
    if (!history.length) return;
    const data = {
      generated: new Date().toISOString(),
      source: "Systems Beyond — Beyond Life Simulator",
      inputs,
      perturbations: forced,
      attractor: { type: attractor.length === 1 ? "fixed_point" : attractor.length ? "limit_cycle" : "none", length: attractor.length, stepsTo: attractorStart, fate: BN.classifyFate(attractor[0]) },
      nodes: BN.ALL_NODES,
      trajectory: history.map((st) => BN.ALL_NODES.map((n) => (st[n] ? 1 : 0))),
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `beyond-life-run-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 0);
  };

  useEffect(() => {
    if (!playing) return;
    if (step >= history.length - 1) { setPlaying(false); return; }
    timer.current = setTimeout(() => setStep((s) => s + 1), speed);
    return () => timer.current && clearTimeout(timer.current);
  }, [playing, step, history.length, speed]);

  useEffect(() => () => timer.current && clearTimeout(timer.current), []);

  const attractorType = attractor.length === 0 ? "—" : attractor.length === 1 ? "Fixed point" : "Limit cycle";
  const heatmapNodes = keyOnly ? BN.KEY_NODES : BN.ALL_NODES;
  const stale = history.length > 0 && lastRunKey !== JSON.stringify({ inputs, forced });

  return (
    <div className="bg-charcoal text-white min-h-screen">
      <Section className="pt-32 pb-10 lg:pt-40" >
        <Link to="/independent-research" className="inline-flex items-center gap-2 font-mono-sci text-xs text-white/50 hover:text-acid transition-colors mb-8" data-testid="sim-back">
          <ArrowLeft size={14} /> BEYOND LIFE / INDEPENDENT RESEARCH
        </Link>
        <div className="max-w-3xl">
          <Overline dark>Interactive prototype</Overline>
          <h1 className="mt-6 text-4xl lg:text-6xl tracking-tighter leading-[0.98] font-medium">Beyond Life Simulator</h1>
          <p className="mt-6 text-white/60 leading-relaxed">
            A logical (Boolean) network of a human somatic cell. Set environmental and intracellular
            signals, run the dynamics, and observe which stable cellular state the network settles into —
            proliferation, senescence, apoptosis or an inflammatory SASP.
          </p>
        </div>
      </Section>

      <Section className="pb-28">
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Controls */}
          <div className="lg:col-span-4 space-y-6">
            <div className="border border-white/10 bg-charcoal-muted p-6">
              <p className="overline text-white/40 mb-4">Scenarios</p>
              <div className="grid grid-cols-2 gap-2">
                {Object.entries(BN.PRESETS).map(([key, p]) => (
                  <button
                    key={key}
                    onClick={() => applyPreset(key)}
                    data-testid={`preset-${key}`}
                    className={`text-left p-3 border transition-colors ${activePreset === key ? "border-acid bg-acid/10" : "border-white/10 hover:border-white/30"}`}
                  >
                    <span className="font-mono-sci text-xs text-white">{p.name}</span>
                  </button>
                ))}
              </div>
              {activePreset && <p className="mt-3 text-xs text-white/45 leading-relaxed">{BN.PRESETS[activePreset].description}</p>}
            </div>

            <div className="border border-white/10 bg-charcoal-muted p-6">
              <p className="overline text-white/40 mb-4">Input signals</p>
              <div className="space-y-1.5">
                {BN.INPUT_NODES.map((node) => (
                  <button
                    key={node}
                    onClick={() => toggle(node)}
                    data-testid={`input-${node}`}
                    className="w-full flex items-center justify-between gap-3 py-2 group"
                  >
                    <span className="text-left">
                      <span className="font-mono-sci text-xs text-white block">{node}</span>
                      <span className="text-[11px] text-white/40">{BN.INPUT_DESCRIPTIONS[node]}</span>
                    </span>
                    <span className={`relative w-10 h-5 shrink-0 transition-colors ${inputs[node] ? "bg-acid" : "bg-white/15"}`}>
                      <span className={`absolute top-0.5 w-4 h-4 bg-charcoal transition-all ${inputs[node] ? "left-[22px]" : "left-0.5"}`} />
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="border border-white/10 bg-charcoal-muted p-6 space-y-5">
              <div>
                <div className="flex justify-between mb-2">
                  <span className="overline text-white/40">Max steps</span>
                  <span className="font-mono-sci text-xs text-acid">{maxSteps}</span>
                </div>
                <input type="range" min="10" max="120" value={maxSteps} onChange={(e) => setMaxSteps(+e.target.value)} data-testid="max-steps" className="w-full accent-acid" />
              </div>
              <div>
                <div className="flex justify-between mb-2">
                  <span className="overline text-white/40">Animation speed</span>
                  <span className="font-mono-sci text-xs text-acid">{speed}ms</span>
                </div>
                <input type="range" min="40" max="600" step="20" value={speed} onChange={(e) => setSpeed(+e.target.value)} data-testid="anim-speed" className="w-full accent-acid" />
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={run} data-testid="run-sim" className={`flex-1 inline-flex items-center justify-center gap-2 py-3 text-[13px] font-medium transition-colors ${stale ? "bg-acid text-charcoal animate-pulse" : "bg-acid text-charcoal hover:bg-acid-dark"}`}>
                  <Play size={15} /> Run
                </button>
                <button onClick={reset} data-testid="reset-sim" className="inline-flex items-center justify-center gap-2 px-4 py-3 text-[13px] font-medium border border-white/20 text-white hover:border-white transition-colors">
                  <RotateCcw size={15} />
                </button>
              </div>
              {stale && <p className="text-[11px] text-acid mt-1 font-mono-sci" data-testid="stale-hint">Inputs changed — run to update.</p>}
            </div>

            {/* Perturbations */}
            <div className="border border-white/10 bg-charcoal-muted p-6">
              <p className="overline text-white/40 mb-4">Perturbations</p>
              <p className="text-[11px] text-white/40 mb-4 leading-relaxed">Knock out or force a pathway ON to test genetic and therapeutic interventions.</p>
              <div className="flex gap-2">
                <select value={pertNode} onChange={(e) => setPertNode(e.target.value)} data-testid="pert-select" className="flex-1 bg-charcoal border border-white/15 text-white font-mono-sci text-xs px-3 py-2.5 outline-none focus:border-acid cursor-pointer">
                  <option value="">Select node…</option>
                  {BN.INTERNAL_NODES.filter((n) => !(n in forced)).map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div className="flex gap-2 mt-2">
                <button onClick={() => addPert(false)} data-testid="pert-ko" disabled={!pertNode} className="flex-1 py-2.5 text-[12px] font-medium border border-white/20 text-white hover:border-white transition-colors disabled:opacity-40">Knock out</button>
                <button onClick={() => addPert(true)} data-testid="pert-on" disabled={!pertNode} className="flex-1 py-2.5 text-[12px] font-medium border border-acid/40 text-acid hover:border-acid transition-colors disabled:opacity-40">Force ON</button>
              </div>
              {Object.keys(forced).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {Object.entries(forced).map(([node, val]) => (
                    <button key={node} onClick={() => removePert(node)} data-testid={`pert-chip-${node}`} className="group inline-flex items-center gap-1.5 font-mono-sci text-[11px] px-2.5 py-1.5 border border-white/15 hover:border-acid transition-colors">
                      <span className={val ? "text-acid" : "text-white/60"}>{node} {val ? "ON" : "KO"}</span>
                      <X size={11} className="text-white/40 group-hover:text-acid" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Attractor sampling */}
            <div className="border border-white/10 bg-charcoal-muted p-6">
              <p className="overline text-white/40 mb-4">Attractor sampling</p>
              <p className="text-[11px] text-white/40 mb-4 leading-relaxed">Run 60 random internal initial states under the current environment to map the fate landscape.</p>
              <button onClick={runSample} data-testid="run-sample" className="w-full inline-flex items-center justify-center gap-2 py-3 text-[13px] font-medium border border-white/20 text-white hover:border-acid transition-colors">
                <Shuffle size={15} /> Sample attractors
              </button>
              {sample && (
                <div className="mt-5" data-testid="sample-result">
                  <div className="flex justify-between font-mono-sci text-[11px] text-white/50 mb-3">
                    <span>{sample.distinctAttractors} distinct attractor(s)</span>
                    <span>{sample.totalSamples} samples</span>
                  </div>
                  <div className="space-y-2">
                    {sample.fateDistribution.map((f) => (
                      <div key={f.fate}>
                        <div className="flex justify-between text-[11px] mb-1">
                          <span className="text-white/70">{f.fate}</span>
                          <span className="text-acid font-mono-sci">{Math.round((f.count / sample.totalSamples) * 100)}%</span>
                        </div>
                        <div className="h-1.5 bg-white/10">
                          <div className="h-full bg-acid" style={{ width: `${(f.count / sample.totalSamples) * 100}%` }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Visualization */}
          <div className="lg:col-span-8 space-y-6">
            {/* Fate + metrics */}
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="border border-white/10 bg-charcoal-muted p-6">
                <p className="overline text-white/40 mb-4">Cell fate</p>
                <div className="grid grid-cols-2 gap-2">
                  {FATES.map((f) => {
                    const on = current && current[f.key];
                    return (
                      <div key={f.key} data-testid={`fate-${f.key}`} className={`flex items-center gap-2.5 p-3 border transition-colors ${on ? "border-acid bg-acid/10" : "border-white/10"}`}>
                        <span className={`w-2.5 h-2.5 rounded-full ${on ? "bg-acid pulse-node" : "bg-white/20"}`} />
                        <span className={`text-xs ${on ? "text-white" : "text-white/45"}`}>{f.label}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="border border-white/10 bg-charcoal-muted p-6">
                <p className="overline text-white/40 mb-4">Attractor</p>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <div className="font-mono-sci text-[10px] text-white/40">TYPE</div>
                    <div className="text-lg text-acid mt-1" data-testid="attractor-type">{attractorType}</div>
                  </div>
                  <div>
                    <div className="font-mono-sci text-[10px] text-white/40">LENGTH</div>
                    <div className="text-lg text-white mt-1" data-testid="attractor-length">{attractor.length || "—"}</div>
                  </div>
                  <div>
                    <div className="font-mono-sci text-[10px] text-white/40">STEP {step >= 0 ? step + 1 : ""}</div>
                    <div className="text-lg text-white mt-1">{history.length ? `${step + 1}/${history.length}` : "—"}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="border border-white/10 bg-charcoal-muted">
              <div className="flex items-center justify-between border-b border-white/10 px-4">
                <div className="flex">
                  {[
                    { k: "network", label: "Network", icon: GitCommitVertical },
                    { k: "matrix", label: "State matrix", icon: Grid3x3 },
                  ].map((t) => (
                    <button
                      key={t.k}
                      onClick={() => setTab(t.k)}
                      data-testid={`tab-${t.k}`}
                      className={`flex items-center gap-2 px-4 py-4 text-[13px] transition-colors border-b-2 -mb-px ${tab === t.k ? "border-acid text-white" : "border-transparent text-white/50 hover:text-white"}`}
                    >
                      <t.icon size={15} /> {t.label}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-4 pr-4">
                  {tab === "matrix" && (
                    <label className="flex items-center gap-2 font-mono-sci text-[11px] text-white/50 cursor-pointer">
                      <input type="checkbox" checked={keyOnly} onChange={(e) => setKeyOnly(e.target.checked)} data-testid="key-only" className="accent-acid" />
                      KEY NODES ONLY
                    </label>
                  )}
                  {history.length > 0 && (
                    <button onClick={exportRun} data-testid="export-run" className="inline-flex items-center gap-1.5 font-mono-sci text-[11px] text-white/60 hover:text-acid transition-colors">
                      <Download size={13} /> EXPORT JSON
                    </button>
                  )}
                </div>
              </div>

              <div ref={wrapRef} className="p-4 overflow-x-auto">
                {tab === "network" ? (
                  history.length || current ? (
                    <NetworkCanvas state={current} width={width} />
                  ) : (
                    <Placeholder icon={<Activity size={26} />} text="Run a simulation to see the network dynamics" />
                  )
                ) : history.length ? (
                  <HeatmapCanvas history={history} currentStep={step} nodes={heatmapNodes} width={width} />
                ) : (
                  <Placeholder icon={<Grid3x3 size={26} />} text="Run a simulation to see the state matrix" />
                )}
              </div>
            </div>

            <p className="font-mono-sci text-[11px] text-white/35 leading-relaxed">
              Prototype model for research exploration only. Not medical advice and not experimentally
              validated. Ported from the Beyond Life logical-network prototype.
            </p>
          </div>
        </div>
      </Section>
    </div>
  );
}

function Placeholder({ icon, text }) {
  return (
    <div className="h-[420px] flex flex-col items-center justify-center text-white/30 gap-4">
      {icon}
      <p className="text-sm">{text}</p>
    </div>
  );
}
